/**
 * Onboarding Context
 * Manages the onboarding flow for new businesses
 *
 * Features:
 * - Auto-triggers on first login
 * - Tracks onboarding progress
 * - Persists state across sessions
 * - Guides users through setup steps
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type IndustryTemplate = 'cafe' | 'restaurant' | 'retail' | 'salon' | 'fitness' | 'custom';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  required: boolean;
  order: number;
}

export interface OnboardingData {
  // Industry template
  industry: IndustryTemplate;

  // Rewards configuration
  b3trPerDollar: number;
  minRedemption: number;
  tierThresholds: {
    bronze: number;
    silver: number;
    gold: number;
    platinum: number;
  };
  tierMultipliers: {
    bronze: number;
    silver: number;
    gold: number;
    platinum: number;
  };

  // Sustainability bonuses
  ecoMultiplier: number;
  enableMugshot: boolean;
  enableGreencart: boolean;
  enableBYB: boolean;
}

interface OnboardingContextType {
  isOnboarding: boolean;
  currentStep: number;
  steps: OnboardingStep[];
  progress: number;
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  applyTemplate: (template: IndustryTemplate) => void;
  startOnboarding: () => void;
  completeStep: (stepId: string) => void;
  skipOnboarding: () => void;
  goToStep: (stepNumber: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  finishOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const ONBOARDING_STEPS: Omit<OnboardingStep, 'completed'>[] = [
  {
    id: 'industry-template',
    title: 'Choose Your Industry',
    description: 'Get started with a template designed for your business type',
    required: true,
    order: 1,
  },
  {
    id: 'rewards-setup',
    title: 'Configure Rewards',
    description: 'Set earning rates and tier progression',
    required: true,
    order: 2,
  },
  {
    id: 'ecosystem-bonuses',
    title: 'Sustainability Bonuses',
    description: 'Reward eco-friendly behavior with bonus multipliers',
    required: false,
    order: 3,
  },
  {
    id: 'add-location',
    title: 'Add Location',
    description: 'Create your first location to start earning',
    required: true,
    order: 4,
  },
  {
    id: 'review-launch',
    title: 'Review & Launch',
    description: 'Preview your setup and go live',
    required: true,
    order: 5,
  },
];

// Default onboarding data
const DEFAULT_DATA: OnboardingData = {
  industry: 'custom',
  b3trPerDollar: 10,
  minRedemption: 100,
  tierThresholds: {
    bronze: 0,
    silver: 500,
    gold: 2000,
    platinum: 5000,
  },
  tierMultipliers: {
    bronze: 1.0,
    silver: 1.2,
    gold: 1.5,
    platinum: 2.0,
  },
  ecoMultiplier: 1.5,
  enableMugshot: false,
  enableGreencart: false,
  enableBYB: false,
};

// Industry-specific templates
const INDUSTRY_TEMPLATES: Record<IndustryTemplate, Partial<OnboardingData>> = {
  cafe: {
    industry: 'cafe',
    b3trPerDollar: 10,
    minRedemption: 100,
    tierThresholds: { bronze: 0, silver: 300, gold: 1000, platinum: 3000 },
    tierMultipliers: { bronze: 1.0, silver: 1.25, gold: 1.5, platinum: 2.0 },
    ecoMultiplier: 1.5,
    enableMugshot: true,
  },
  restaurant: {
    industry: 'restaurant',
    b3trPerDollar: 10,
    minRedemption: 200,
    tierThresholds: { bronze: 0, silver: 500, gold: 2000, platinum: 5000 },
    tierMultipliers: { bronze: 1.0, silver: 1.2, gold: 1.5, platinum: 2.0 },
    ecoMultiplier: 1.5,
    enableGreencart: true,
  },
  retail: {
    industry: 'retail',
    b3trPerDollar: 5,
    minRedemption: 500,
    tierThresholds: { bronze: 0, silver: 1000, gold: 5000, platinum: 15000 },
    tierMultipliers: { bronze: 1.0, silver: 1.15, gold: 1.3, platinum: 1.5 },
    ecoMultiplier: 1.3,
    enableBYB: true,
  },
  salon: {
    industry: 'salon',
    b3trPerDollar: 15,
    minRedemption: 150,
    tierThresholds: { bronze: 0, silver: 400, gold: 1500, platinum: 4000 },
    tierMultipliers: { bronze: 1.0, silver: 1.3, gold: 1.6, platinum: 2.0 },
    ecoMultiplier: 1.4,
    enableBYB: true,
  },
  fitness: {
    industry: 'fitness',
    b3trPerDollar: 20,
    minRedemption: 200,
    tierThresholds: { bronze: 0, silver: 600, gold: 2500, platinum: 7000 },
    tierMultipliers: { bronze: 1.0, silver: 1.25, gold: 1.5, platinum: 1.75 },
    ecoMultiplier: 1.6,
    enableBYB: true,
  },
  custom: {
    industry: 'custom',
  },
};

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<OnboardingStep[]>(
    ONBOARDING_STEPS.map(step => ({ ...step, completed: false }))
  );
  const [data, setData] = useState<OnboardingData>(DEFAULT_DATA);

  // Calculate progress percentage
  const progress = Math.round((steps.filter(s => s.completed).length / steps.length) * 100);

  // Load onboarding state from localStorage
  useEffect(() => {
    if (user) {
      const savedState = localStorage.getItem(`onboarding_${user.id}`);
      if (savedState) {
        const { isOnboarding: saved, currentStep: savedStep, steps: savedSteps } = JSON.parse(savedState);
        setIsOnboarding(saved);
        setCurrentStep(savedStep);
        setSteps(savedSteps);
      } else {
        // First time user - check if they need onboarding
        checkNeedsOnboarding();
      }
    }
  }, [user]);

  // Save onboarding state to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(
        `onboarding_${user.id}`,
        JSON.stringify({ isOnboarding, currentStep, steps })
      );
    }
  }, [user, isOnboarding, currentStep, steps]);

  // Check if user needs onboarding
  const checkNeedsOnboarding = async () => {
    try {
      // TODO: Call API to check if business setup is complete
      // For now, check if user just registered (mock logic)
      const hasCompletedSetup = localStorage.getItem(`setup_complete_${user?.id}`);

      if (!hasCompletedSetup) {
        // New user - start onboarding automatically
        setTimeout(() => {
          setIsOnboarding(true);
        }, 500); // Small delay for better UX
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const startOnboarding = () => {
    setIsOnboarding(true);
    setCurrentStep(0);
    setSteps(ONBOARDING_STEPS.map(step => ({ ...step, completed: false })));
  };

  const completeStep = (stepId: string) => {
    setSteps(prevSteps =>
      prevSteps.map(step =>
        step.id === stepId ? { ...step, completed: true } : step
      )
    );
  };

  const skipOnboarding = () => {
    setIsOnboarding(false);
    if (user) {
      localStorage.setItem(`setup_complete_${user.id}`, 'skipped');
    }
  };

  const goToStep = (stepNumber: number) => {
    if (stepNumber >= 0 && stepNumber < steps.length) {
      setCurrentStep(stepNumber);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      finishOnboarding();
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateData = (updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const applyTemplate = (template: IndustryTemplate) => {
    const templateData = INDUSTRY_TEMPLATES[template];
    setData(prev => ({ ...prev, ...templateData }));
  };

  const finishOnboarding = () => {
    setIsOnboarding(false);
    if (user) {
      localStorage.setItem(`setup_complete_${user.id}`, 'completed');
    }

    // Show success message
    // TODO: Integrate with toast/notification system
    console.log('Onboarding completed!');
  };

  const value: OnboardingContextType = {
    isOnboarding,
    currentStep,
    steps,
    progress,
    data,
    updateData,
    applyTemplate,
    startOnboarding,
    completeStep,
    skipOnboarding,
    goToStep,
    nextStep,
    previousStep,
    finishOnboarding,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
